import cx from 'classnames';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {ModalProvider} from 'react-modal-hook';
import {Provider, shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Route, Router, Switch, useLocation} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import {TransitionGroup} from 'react-transition-group';
import {ErrorBoundary} from '~/common/components/ErrorBoundary/ErrorBoundary';
import {useAppTagging} from '~/common/components/ErrorBoundary/useAppTagging';
import {InjectableMegaNav} from '~/common/components/MegaNav/InjectableMegaNav';
import {INIT_APP} from '~/common/fabric/constants';
import {useLazyGetTemplatesQuery} from '~/SignUpSheets/api/content';
import {useLazyGetBlueprintQuery} from '~/SignUpSheets/api/sheet';
import {useGetAllThemesQuery} from '~/SignUpSheets/api/signUpSheets';
import {Customize} from '~/SignUpSheets/Create/routes/Customize/Customize';
import {HostConfirm} from '~/SignUpSheets/Create/routes/HostConfirm/HostConfirm';
import {Header} from '~/SignUpSheets/components/Header/Header';
import {SignUpSheetContextParent} from '~/SignUpSheets/context/SignUpSheetContext';
import {history} from '~/SignUpSheets/history';
import {useAffiliateActions} from '~/SignUpSheets/hooks/useAffiliateActions';
import {useSheetActions} from '~/SignUpSheets/hooks/useSheetActions';
import {SET_BLUEPRINT} from '~/SignUpSheets/reducers/blueprint/constants';
import {selectBlueprintId} from '~/SignUpSheets/reducers/blueprint/selectors';
import {selectIsReady} from '~/SignUpSheets/reducers/selectors';
import {
  SCOPE_HOST,
  SET_SHEET,
  UPDATE_OPEN_GROUPS,
  UPDATE_SHEET_DETAILS,
} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectSignupOptionGroupIds,
  selectSignupOptionUuids,
  selectThemeId,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {store} from '~/SignUpSheets/reducers/store';
import {SET_THEMES} from '~/SignUpSheets/reducers/themes/constants';
import {selectBackground, selectThemes} from '~/SignUpSheets/reducers/themes/selectors';
import {schemaToJson} from '~/SignUpSheets/utils/schema';
import {Video} from '~library/atoms/Video';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {InjectableFooter} from '~library/organisms/InjectableFooter';
import {LoginModalWrapper} from '~library/organisms/LoginModal/LoginModalWrapper';
import {hideLoginModal} from '~library/organisms/LoginModal/reducer';
import {getUserData} from '~library/utils/auth/utils';

import 'react-toastify/dist/ReactToastify.min.css';
import '~/SignUpSheets/app.sass';

const ANIMATION_TIME = 350;

store.dispatch({
  type: INIT_APP,
  payload: {
    user: getUserData(),
  },
});

let appInit = false;
const App = () => {
  const modifiedGroups = useRef([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname.match(/\/(?<path>[a-z]+$)/)?.groups.path || 'create';
  const [showSiteNav, setShowSiteNav] = useState(false);
  const isDesktop = useIsLargishAndUp();
  const selectedBackground = useSelector((s) => selectBackground(s, {isDesktop}));
  const themeOptions = useSelector(selectThemes, shallowEqual);
  const ready = useSelector(selectIsReady);
  const blueprintId = useSelector(selectBlueprintId);
  const themeId = useSelector(selectThemeId);
  const [getBlueprint] = useLazyGetBlueprintQuery();
  const {getSavedSheet} = useSheetActions();
  const {data: themes = []} = useGetAllThemesQuery();
  const [getTemplates] = useLazyGetTemplatesQuery();
  const {processProductLinks} = useAffiliateActions();
  const signupOptionUuids = useSelector(selectSignupOptionUuids(), shallowEqual);
  const signupOptionGroupIds = useSelector(selectSignupOptionGroupIds(), shallowEqual);
  const prevNumSignupOptions = useRef(signupOptionUuids.length);
  const prevNumGroups = useRef(signupOptionGroupIds.length);

  useAppTagging({
    isDesktop,
    blueprintId,
    themeId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    dispatch(hideLoginModal());
  }, []);

  useEffect(() => {
    if (themes?.length > 0) {
      dispatch({type: SET_THEMES, payload: themes});
    }
  }, [dispatch, themes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (path !== 'create' || themeOptions.length === 0) return;
    if (!themeOptions.flatMap((el) => el.themes).some(({theme_id: tid}) => tid === themeId)) {
      dispatch({
        type: UPDATE_SHEET_DETAILS,
        // biome-ignore lint/style/useNamingConvention: existing error
        payload: {theme_id: window.blueprint.theme_id},
      });
    }
  }, [themeOptions, themeId, dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    setShowSiteNav(path === 'success');
  }, [path, setShowSiteNav]);

  useEffect(() => {
    if (path === 'create' || !blueprintId) return;
    getBlueprint(blueprintId)
      .then((res) => {
        dispatch({
          type: SET_BLUEPRINT,
          payload: res.data.data,
        });
      })
      .catch(evite.error);
  }, [dispatch, getBlueprint, blueprintId, path]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!appInit) {
      appInit = true;
      if (path === 'create' && window.blueprint) {
        dispatch({
          type: SET_BLUEPRINT,
          payload: window.blueprint,
        });
        const searchParams = new URLSearchParams(location.search);
        const theme = searchParams.get('theme') ?? window.blueprint.theme_id;
        dispatch({
          type: SET_SHEET,
          payload: {
            // biome-ignore lint/style/useNamingConvention: existing error
            sheet_type: window.blueprint.blueprint_type,
            // biome-ignore lint/style/useNamingConvention: existing error
            blueprint_id: window.blueprint_id,
            // biome-ignore lint/style/useNamingConvention: existing error
            signup_options: [
              schemaToJson(
                window.blueprint.field_schema,
                window.blueprint.blueprint_type,
                SCOPE_HOST
              ),
            ],
            // biome-ignore lint/style/useNamingConvention: existing error
            theme_id: theme,
          },
        });
        const templateId = searchParams.get('template');
        if (!templateId) return;
        getTemplates({
          id: templateId,
          type: window.blueprint.blueprint_type,
          schema: window.blueprint.field_schema,
        }).then(({data: template}) => {
          if (!template) return;
          processProductLinks(template.signup_options);
          dispatch({
            type: SET_SHEET,
            payload: template,
          });
        });
        // we grab the sheet in edit mode in Customize
      } else if (path !== 'edit') {
        getSavedSheet();
      }
    }
  }, [location, path, getTemplates, processProductLinks]);

  const isSuccess = location.pathname.includes('success');
  const MemoizedVideo = memo(Video);
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const renderedBackground = useCallback(
    () => (
      <div
        className="signup-sheet__background-wrapper"
        style={{backgroundImage: !isSuccess && selectedBackground}}
      >
        {!isSuccess && /\.(mp4|webm)$/i.test(selectedBackground) && (
          <MemoizedVideo
            src={selectedBackground}
            className="signup-sheet__video-background"
            options={{
              key: selectedBackground.src,
            }}
          />
        )}
      </div>
    ),
    [selectedBackground]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (signupOptionUuids.length > 0) {
      if (isDesktop) {
        if (signupOptionGroupIds.length > 0) {
          dispatch({
            type: UPDATE_OPEN_GROUPS,
            payload: signupOptionGroupIds,
          });
        } else {
          dispatch({
            type: UPDATE_OPEN_GROUPS,
            payload: signupOptionUuids,
          });
        }
      } else if (modifiedGroups.current.length > 0) {
        dispatch({
          type: UPDATE_OPEN_GROUPS,
          payload: modifiedGroups.current,
        });
      } else if (signupOptionGroupIds.length > 0) {
        dispatch({
          type: UPDATE_OPEN_GROUPS,
          payload: [signupOptionGroupIds[0]],
        });
      } else {
        dispatch({
          type: UPDATE_OPEN_GROUPS,
          payload: [signupOptionUuids[0]],
        });
      }
    }

    // Jump to a group if specified (e.g. new group)
    if (modifiedGroups.current.length > 0) {
      const jumpToGroup =
        signupOptionGroupIds.length > 0
          ? document.getElementById(`group-${modifiedGroups.current[0]}`)
          : document.getElementById(`signup-option-${modifiedGroups.current[0]}`);
      if (jumpToGroup) {
        if (
          (signupOptionGroupIds.length > 0 &&
            signupOptionGroupIds.length !== prevNumGroups.current) ||
          (signupOptionGroupIds.length === 0 &&
            signupOptionUuids.length !== prevNumSignupOptions.current)
        ) {
          // A new ungrouped slot, or a new group has been created (i.e. not a new slot inside a group); scroll into view
          setTimeout(() => {
            jumpToGroup.scrollIntoView({behavior: 'smooth', block: 'start'});
          }, ANIMATION_TIME);
        }
      }
      modifiedGroups.current = [];
    }
    prevNumSignupOptions.current = signupOptionUuids.length;
    prevNumGroups.current = signupOptionGroupIds.length;
  }, [isDesktop, signupOptionGroupIds, signupOptionUuids]);

  const trackChangeUpstream = useCallback((groups = []) => {
    modifiedGroups.current = groups;
  }, []);

  const CustomizePage = useCallback(
    () => <Customize trackChangeUpstream={trackChangeUpstream} />,
    [trackChangeUpstream]
  );

  return (
    <>
      {/* Only include Redux login modal wrapper if MegaNav is not included */}
      {showSiteNav ? null : <LoginModalWrapper />}
      <SignUpSheetContextParent>
        {showSiteNav ? <InjectableMegaNav /> : <Header trackChangeUpstream={trackChangeUpstream} />}
        <div
          className={cx(path, 'signup-sheet__page-wrapper', {
            'signup-sheet__page-wrapper--no-padding': showSiteNav,
          })}
        >
          {path === 'success' ? null : renderedBackground()}
          <Switch>
            <Route
              exact
              path={['/:sheetId/customize', '/:sheetId/edit', '/:template/create']}
              component={CustomizePage}
            />
            <Route exact path="/:sheetId/success" component={HostConfirm} />
          </Switch>
        </div>
        <LoadingOverlay
          show={!ready}
          loadingMessage={path === 'create' ? 'Loading...' : 'Loading sheet...'}
        />
        {showSiteNav && <InjectableFooter />}
      </SignUpSheetContextParent>
    </>
  );
};

const CreateSignUpSheetAppWrapper = () => (
  <ErrorBoundary componentName="CreateSignUpSheetAppWrapper">
    <Provider store={store}>
      <ModalProvider rootComponent={TransitionGroup}>
        <Router history={history}>
          <App />
        </Router>

        <ToastContainer
          position="bottom-right"
          autoClose={6000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          icon={false}
        />
      </ModalProvider>
    </Provider>
  </ErrorBoundary>
);

const root = createRoot(document.getElementById('signup-sheet-root'));
root.render(<CreateSignUpSheetAppWrapper />);
